import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ComponentBaseComponent } from '../component-base/component-base.component';
import { Base64UrlService } from '@app/services/base64url.service';
import { UpgradeModule } from '@angular/upgrade/static';

@Component({
    selector: 'tt-chat',
    templateUrl: './chat.component.html',
    styleUrls: ['./chat.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatComponent<TType extends Comment> extends ComponentBaseComponent implements OnChanges {
    @Input()
    public ttOptions?: ChatOptions<TType>;

    @Output()
    public change = new EventEmitter();

    public comment: string = '';

    public editorProcessing = false;

    public comments: TType[] = [];

    public saving: boolean = false;

    public loadingComments: boolean = false;

    private base64: any;

    constructor(
        private datatask: DataTaskService,
        private cdr: ChangeDetectorRef,
        private upgrade: UpgradeModule,
    ) {
        super();
    }

    public trackItems(_: number, item: TType) {
        return item.reg_datetime + item.registered_by_name;
    }

    public async loadComments() {
        if (this.validDataTask(this.ttOptions?.fetch.get)) {
            try {
                this.loadingComments = true;
                let parameters = await this.getParameters(this.ttOptions.fetch.get.parameters);

                this.comments = await this.datatask.Post(this.ttOptions.fetch.get.method, parameters);
                this.cdr.markForCheck();
            } finally {
                this.loadingComments = false;
            }
        }
    }

    public async addNewComment() {
        if (this.validDataTask(this.ttOptions?.fetch.save)) {
            try {
                this.saving = true;
                let parameters = { ...(await this.getParameters(this.ttOptions.fetch.save.parameters)), messagetext: this.getBase64().encode(this.comment) };

                await this.datatask.Post(this.ttOptions.fetch.save.method, parameters);

                this.comment = '';
                this.cdr.markForCheck();
                this.change.emit();
            } finally {
                this.saving = false;
                this.loadComments();
            }
        }
    }

    public async deleteComment(id: TType[keyof TType]) {
        if (this.validDataTask(this.ttOptions?.fetch.delete)) {
            try {
                let parameters = { ...(await this.getParameters(this.ttOptions.fetch.delete.parameters)), [this.ttOptions.keynames.commentKeyno]: id };

                await this.datatask.Post(this.ttOptions.fetch.delete.method, parameters);
                this.cdr.markForCheck();
                this.change.emit();
            } finally {
                this.loadComments();
            }
        }
        //
    }

    private validDataTask(value: unknown): value is DataTaskRequest {
        return !!value && typeof value === 'object' && Object.hasOwn(value, 'method') && !!(value as any).method && !isNaN(+(value as any).method);
    }

    private async getParameters(params: undefined | any | (() => any)) {
        let parameters = params;

        if (parameters instanceof Function) {
            parameters = parameters();
        }

        if (parameters instanceof Promise) {
            parameters = await parameters;
        }

        return parameters;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ttOptions']) {
            if (JSON.stringify(changes['ttOptions'].currentValue?.fetch?.get || '') !== JSON.stringify(changes['ttOptions'].previousValue?.fetch?.get || '')) {
                this.loadComments();
            }
        }
    }

    public getBase64(): any {
        if (!this.base64) {
            const $injector = this.upgrade.injector.get('$injector');
            this.base64 = $injector.get('base64');
        }

        return this.base64;
    }
}

export interface Comment {
    registered_by_name: string;
    messagetext: string;
    reg_datetime: string;
    orderby: string | number;
    mymessage: '1' | '0';
}

interface DataTaskRequest {
    method: number;
    parameters?: any | (() => any);
}

export interface ChatOptions<TType extends Comment> {
    /**
     * Mapping of property keys to the comment object.
     */
    keynames: {
        /**
         * The key of the property which contains the id of the comment.
         */
        commentKeyno: keyof TType;
    };

    /**
     * Configuration of datatask requests of the chat.
     */
    fetch: {
        /**
         * Datatask information for the procedure returning a list of `Comment`.
         */
        get: DataTaskRequest;

        /**
         * Datatask information for saving a new added comment. `'messagetext'` will be added as base64 encoded parameter for the procedurecall.
         */
        save: DataTaskRequest;

        /**
         * Options datatask information for deleting a comment.
         */
        delete?: DataTaskRequest;
    };
}
